import type { TenantMedia } from '@avrahami/contracts';
import {
  Button,
  countText,
  FileDropCard,
  IMAGE_UPLOAD_ACCEPT,
  PlusIcon,
  SvgImageError,
  uploadableImage,
} from '@avrahami/ui';
import { useState } from 'react';
import { platformApi } from '../api/platform';
import { he } from '../i18n/he';
import { errorMessage } from '../lib/format';
import { uploadProblem } from '../lib/product-page';

/**
 * Uploads images of a store one after another, in the order given, and hands each stored media
 * to the caller. Nothing is uploaded when a file is wrong or there are more than `limit` of them.
 * `start` answers whether all of them were stored.
 */
function useImageUpload(tenantId: string, onUploaded: (media: TenantMedia) => void, limit: number) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const start = async (files: readonly File[]): Promise<boolean> => {
    setError(undefined);
    if (files.length > limit) {
      setError(countText(limit, he.upload.tooMany));
      return false;
    }
    const found = files.map(uploadProblem).find((problem) => problem !== null);
    if (found !== undefined) {
      setError(found === 'TOO_LARGE' ? he.upload.tooLarge : he.upload.notImage);
      return false;
    }
    setBusy(true);
    try {
      for (const file of files) {
        onUploaded(await platformApi.uploadTenantMedia(tenantId, await uploadableImage(file)));
      }
      return true;
    } catch (failure) {
      setError(failure instanceof SvgImageError ? he.upload.badSvg : errorMessage(failure));
      return false;
    } finally {
      setBusy(false);
    }
  };
  return {
    start,
    busy,
    error,
    clear: () => {
      setError(undefined);
    },
  };
}

/**
 * Adds an image of the store (adr/0043): a button that opens a card to drag the image onto or to
 * choose it from the computer. The card closes once the upload is stored. `limit` is how many
 * images may be added at once (the test hook is on the file input, `<testId>-open` on the button).
 */
export function ImageAdder({
  tenantId,
  label,
  onUploaded,
  limit = 1,
  testId,
}: {
  tenantId: string;
  label: string;
  onUploaded: (media: TenantMedia) => void;
  limit?: number;
  testId?: string;
}) {
  const [state, setState] = useState<'initial' | 'open' | 'closed'>('initial');
  const upload = useImageUpload(tenantId, onUploaded, limit);
  if (state !== 'open') {
    return (
      <div>
        <Button
          tone="secondary"
          // Closing the card returns the keyboard to the button that opened it.
          autoFocus={state === 'closed'}
          data-testid={testId === undefined ? undefined : `${testId}-open`}
          onClick={() => {
            setState('open');
          }}
        >
          <PlusIcon />
          {label}
        </Button>
      </div>
    );
  }
  return (
    <FileDropCard
      title={label}
      words={{
        drop: limit > 1 ? he.upload.dropMany : he.upload.drop,
        or: he.upload.or,
        choose: he.upload.chooseFile,
        busy: he.upload.uploading,
        close: he.upload.close,
      }}
      accept={IMAGE_UPLOAD_ACCEPT}
      multiple={limit > 1}
      busy={upload.busy}
      hint={he.upload.formats}
      error={upload.error}
      onFiles={(files) => {
        void upload.start(files).then((stored) => {
          if (stored) setState('closed');
        });
      }}
      onClose={() => {
        upload.clear();
        setState('closed');
      }}
      {...(testId === undefined ? {} : { testId })}
    />
  );
}
